import { InteractionStatus, type IPublicClientApplication } from "@azure/msal-browser"

import {
  AuthMode,
  checkEasyAuth,
  getRedirectUri,
  getToken,
  handleAuthRedirect,
  loginRequest,
  refreshEasyAuthToken,
} from "@/api/auth-api"
import { logger } from "@/lib/logger"

import {
  currentPathWithQueryHash,
  MSAL_LOGIN_START_KEY,
  RETURN_TO_KEY,
} from "../utils/auth-constants"
import { storeAuthError } from "../utils/auth-error-store"

export type AuthProvider = "easyauth" | "msal" | "teams"

export type AuthOutcome =
  | { status: "authenticated"; token: string; usingMsalBackup: boolean }
  | { status: "redirecting" }
  | { status: "error"; reason: string; message?: string }

export type AuthContext = {
  msalInstance: IPublicClientApplication
  inProgress: InteractionStatus
}

export interface AuthAdapter {
  provider: AuthProvider
  ensureAuthenticated(ctx: AuthContext): Promise<AuthOutcome>
}

const easyAuthAdapter: AuthAdapter = {
  provider: "easyauth",
  async ensureAuthenticated(): Promise<AuthOutcome> {
    const isAuthenticated = await checkEasyAuth()
    if (isAuthenticated) {
      return { status: "authenticated", token: "easyauth-token", usingMsalBackup: false }
    }

    try {
      const refreshed = await refreshEasyAuthToken()
      if (refreshed) {
        const stillAuthenticated = await checkEasyAuth()
        if (stillAuthenticated) {
          return { status: "authenticated", token: "easyauth-token", usingMsalBackup: false }
        }
      }
    } catch (error) {
      logger.warn("EasyAuth refresh check failed; redirecting to login", error as Error)
    }

    // Full-page navigation avoids CORS issues from auth redirects.
    handleAuthRedirect()
    return { status: "redirecting" }
  },
}

const msalAdapter: AuthAdapter = {
  provider: "msal",
  async ensureAuthenticated({ msalInstance, inProgress }: AuthContext): Promise<AuthOutcome> {
    // Ensure there is an active account if any accounts exist.
    const accounts = msalInstance.getAllAccounts()
    if (!msalInstance.getActiveAccount() && accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0])
    }
    const activeAccount = msalInstance.getActiveAccount()

    // If we have an account, try to get an API token silently.
    if (activeAccount) {
      try {
        const tokenResult = await getToken(msalInstance)
        if (tokenResult) {
          sessionStorage.removeItem(MSAL_LOGIN_START_KEY)
          return { status: "authenticated", token: tokenResult.accessToken, usingMsalBackup: true }
        }
      } catch (error) {
        logger.warn("MSAL silent token acquisition failed; will redirect to login", error as Error)
      }
    }

    // If an interaction is already in progress, wait.
    if (inProgress !== InteractionStatus.None) {
      return { status: "redirecting" }
    }

    // Guard against StrictMode double-mounts / fast re-renders while redirect is pending.
    const lastStart = Number(sessionStorage.getItem(MSAL_LOGIN_START_KEY) ?? "0")
    if (Number.isFinite(lastStart) && Date.now() - lastStart < 60_000) {
      return { status: "redirecting" }
    }

    try {
      sessionStorage.setItem(MSAL_LOGIN_START_KEY, String(Date.now()))
      sessionStorage.setItem(RETURN_TO_KEY, currentPathWithQueryHash())

      await msalInstance.loginRedirect({
        ...loginRequest,
        redirectUri: getRedirectUri(),
      })

      return { status: "redirecting" }
    } catch (error) {
      sessionStorage.removeItem(MSAL_LOGIN_START_KEY)
      const message = error instanceof Error ? error.message : "Unknown error"
      storeAuthError({
        reason: "MSAL sign-in failed",
        message,
        provider: "msal",
        returnTo: sessionStorage.getItem(RETURN_TO_KEY) ?? "/",
      })
      return { status: "error", reason: "MSAL sign-in failed", message }
    }
  },
}

/**
 * Factory for the active auth adapter. Adding Teams SSO later should only require
 * introducing a `teams` adapter and updating this mapping.
 */
export function getAuthAdapter(authMode: AuthMode): AuthAdapter {
  switch (authMode) {
    case AuthMode.EASYAUTH:
      return easyAuthAdapter
    case AuthMode.MSAL:
      return msalAdapter
    default:
      return msalAdapter
  }
}
